import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ActionType } from 'typesafe-actions'
import { NAMESPACE, SUSPENSE_LIST_DEFAULT_QUERIES } from './constants'
import { getTaggablePartyId } from '../party/utils'
import { TPartyEntity } from '../party/types'
import { events as walletEvents } from '../wallet/state'
import {
  TAccountsReceivableRequest,
  TActiveLeasesRequest,
  TAgencyPerformanceSummary,
  TAgencyPerformanceSummaryRequest,
  TBaseReportRequest,
  TCashDepositStatementRequest,
  TDataTypeOnlyRequest,
  TDepositSummaryStats,
  TDepositsRequest,
  TDownloadCreditNotePdfRequest,
  TFranchisePerformanceRequest,
  TOwnerStatementRequest,
  TPropertyTaxStatementRequest,
  TQuickSearchRequest,
  TQuickSearchSuccess,
  TRentRollRequest,
  TReportDownloadResponse,
  TReportUsageRequest,
  TReportingProperty,
  TReportingState,
  TSuspenseAccountStats,
  TSuspenseListKey,
  TSuspenseListQuery,
  TSuspenseListResponse,
  TSuspenseReferenceLookup,
  TTenancyScheduleRequest,
  TTpnReportRequest,
} from './types'

const initialState: TReportingState = {
  depositSummaryStats: {},
  performanceSummary: {
    daily: [],
    weekly: [],
    monthly: [],
    yearly: [],
  },
  properties: [],
  parties: {},
  suspenseAccountLists: {
    unallocated: { query: { ...SUSPENSE_LIST_DEFAULT_QUERIES.unallocated }, items: [], total: 0, totalPages: 0 },
    allocated: { query: { ...SUSPENSE_LIST_DEFAULT_QUERIES.allocated }, items: [], total: 0, totalPages: 0 },
  },
  suspenseReferenceLookups: {},
  suspenseAllocatedTxIds: [],
}

const slice = createSlice({
  name: NAMESPACE,
  initialState,
  reducers: {
    accountsReceivable_request: (state, { payload }: PayloadAction<TAccountsReceivableRequest>) => {},
    accountsReceivable_success: (state, { payload }: PayloadAction<TReportDownloadResponse>) => {},
    commissionSplit_request: (state, { payload }: PayloadAction<TBaseReportRequest>) => {},
    commissionSplit_success: (state, { payload }: PayloadAction<TReportDownloadResponse>) => {},
    agencyCommission_request: (state, { payload }: PayloadAction<TBaseReportRequest>) => {},
    agencyCommission_success: (state, { payload }: PayloadAction<TReportDownloadResponse>) => {},
    agentCommissionReport_request: (state, { payload }: PayloadAction<TBaseReportRequest>) => {},
    agentCommissionReport_success: (state, { payload }: PayloadAction<TReportDownloadResponse>) => {},
    agentCommission_request: (state, { payload }: PayloadAction<TBaseReportRequest>) => {},
    agentCommission_success: (state, { payload }: PayloadAction<TReportDownloadResponse>) => {},
    rentRoll_request: (state, { payload }: PayloadAction<TRentRollRequest>) => {},
    rentRoll_success: (state, { payload }: PayloadAction<TReportDownloadResponse>) => {},
    cashFlow_request: (state, { payload }: PayloadAction<TBaseReportRequest>) => {},
    cashFlow_success: (state, { payload }: PayloadAction<TReportDownloadResponse>) => {},
    deposits_request: (state, { payload }: PayloadAction<TDepositsRequest>) => {},
    deposits_success: (state, { payload }: PayloadAction<TReportDownloadResponse>) => {},
    depositSummaryStats_request: state => {},
    depositSummaryStats_success: (state, { payload }: PayloadAction<TDepositSummaryStats>) => {
      state.depositSummaryStats = payload
    },
    tenancySchedule_request: (state, { payload }: PayloadAction<TTenancyScheduleRequest>) => {},
    tenancySchedule_success: (state, { payload }: PayloadAction<TReportDownloadResponse>) => {},
    agencyGroupCommissionSplit_request: (state, { payload }: PayloadAction<TBaseReportRequest>) => {},
    agencyGroupCommissionSplit_success: (state, { payload }: PayloadAction<TReportDownloadResponse>) => {},
    tpnReport_request: (state, { payload }: PayloadAction<TTpnReportRequest>) => {},
    tpnReport_success: (state, { payload }: PayloadAction<TReportDownloadResponse>) => {},
    cashDepositStatement_request: (state, { payload }: PayloadAction<TCashDepositStatementRequest>) => {},
    cashDepositStatement_success: (state, { payload }: PayloadAction<Record<string, never>>) => {},
    billingReport_request: (state, { payload }: PayloadAction<TBaseReportRequest>) => {},
    billingReport_success: (state, { payload }: PayloadAction<TReportDownloadResponse>) => {},
    agencyPerformanceSummaryChart_request: (state, { payload }: PayloadAction<TAgencyPerformanceSummaryRequest>) => {},
    agencyPerformanceSummaryChart_success: (
      state,
      { payload: { bucketType = 'monthly', buckets = [] } }: PayloadAction<TAgencyPerformanceSummary>,
    ) => {
      state.performanceSummary[bucketType] = buckets
    },
    properties_request: state => {},
    properties_success: (state, { payload }: PayloadAction<TReportingProperty[]>) => {
      state.properties = payload
    },
    quickSearch_request: {
      reducer: (_state, _action: PayloadAction<TQuickSearchRequest>) => {},
      prepare: ({ query = '', tags = '', offset = 0, limit = 10000 }: Partial<TQuickSearchRequest>) => ({
        payload: { query, tags, offset, limit },
      }),
    },
    quickSearch_success: (state, { payload: { results } }: PayloadAction<TQuickSearchSuccess>) => {
      state.parties = {
        ...state.parties,
        ...results.reduce((acc: { [partyId: string]: TPartyEntity }, party) => {
          const id = getTaggablePartyId(party)
          acc[id] = { id, ...party }
          return acc
        }, {}),
      }
    },
    propertyTaxStatement_request: (state, { payload: { start, finish, ownerId, ownerName } }) => {},
    propertyTaxStatement_success: (state, { payload }) => {},
    ownerStatement_request: (state, { payload: { start, finish, ownerId, ownerName } }) => {},
    ownerStatement_success: (state, { payload }) => {},
    franchisePerformance_request: (state, { payload: { dataType = 'excel', start, end, groupId } }) => {},
    franchisePerformance_success: (state, { payload }) => {},
    royaltyReport_request: (state, { payload: { dataType = 'excel', start, finish, groupId } }) => {},
    royaltyReport_success: (state, { payload }) => {},
    reportUsage_request: (
      state,
      { payload: { dataType = 'excel', start, finish, include_dashboard = false, source = 'auto' } },
    ) => {},
    reportUsage_success: (state, { payload }) => {},
    activeAgencies_request: (state, { payload: { dataType = 'excel' } }) => {},
    activeAgencies_success: (state, { payload }) => {},
    activeLeases_request: (state, { payload: { dataType = 'excel', start, finish } }) => {},
    activeLeases_success: (state, { payload }) => {},
    availableFunds_request: (state, { payload: { dataType = 'excel' } }) => {},
    availableFunds_success: (state, { payload }) => {},
    ficaCompliance_request: (state, { payload: { dataType = 'excel' } }) => {},
    ficaCompliance_success: (state, { payload }) => {},
    suspenseAccountAllocated_request: (state, { payload: { dataType = 'excel', start, finish } }) => {},
    suspenseAccountAllocated_success: (state, { payload }) => {},
    suspenseAccountUnallocated_request: (state, { payload: { dataType = 'excel', start, finish } }) => {},
    suspenseAccountUnallocated_success: (state, { payload }) => {},
    suspenseAccountCsv_request: (state, { payload: { dataType = 'text/csv' } }) => {},
    suspenseAccountCsv_success: (state, { payload }) => {},
    suspenseAccountStats_request: () => {},
    suspenseAccountStats_success: (state, { payload }: PayloadAction<TSuspenseAccountStats>) => {
      state.suspenseAccountStats = payload
    },
    /** Merges `query` into what the list last asked for, so sort and page show before the response lands. */
    suspenseAccountList_request: (
      state,
      { payload }: PayloadAction<{ list: TSuspenseListKey; query?: Partial<TSuspenseListQuery> }>,
    ) => {
      const list = state.suspenseAccountLists[payload.list]
      list.query = { ...list.query, ...payload.query }
    },
    suspenseReferenceLookup_request: (state, { payload }: PayloadAction<{ reference: string }>) => {},
    /** `result` is absent when the reference doesn't match a wallet. */
    suspenseReferenceLookup_success: (state, { payload }: PayloadAction<TSuspenseReferenceLookup | undefined>) => {
      if (payload?.reference) {
        state.suspenseReferenceLookups[payload.reference] = payload
      }
    },
    suspenseAccountList_success: (
      state,
      { payload }: PayloadAction<{ list: TSuspenseListKey; response: TSuspenseListResponse }>,
    ) => {
      const { items, total, totalPages, page, pageSize, sort, order, search } = payload.response
      let visibleItems = items
      let visibleTotal = total

      if (payload.list === 'unallocated' && state.suspenseAllocatedTxIds.length > 0) {
        // A receipt allocated here can still come back unallocated while the read side catches up.
        const allocated = new Set(state.suspenseAllocatedTxIds)
        visibleItems = items.filter(item => !allocated.has(item.txId))
        visibleTotal = Math.max(0, total - (items.length - visibleItems.length))
        // The unallocated list is fetched whole, so without a search, a receipt missing from it has
        // caught up and needn't be hidden any more.
        if (!search) {
          const listed = new Set(items.map(item => item.txId))
          state.suspenseAllocatedTxIds = state.suspenseAllocatedTxIds.filter(txId => listed.has(txId))
        }
      }

      state.suspenseAccountLists[payload.list] = {
        query: { page, pageSize, sort, order, search: search ?? '' },
        items: visibleItems,
        total: visibleTotal,
        totalPages,
      }
    },
    wallets_request: (state, { payload: { dataType = 'excel' } }) => {},
    wallets_success: (state, { payload }) => {},
    downloadCreditNotePdf_request: (state, { payload: { invoiceId, invoiceNumber } }) => {},
    downloadCreditNotePdf_success: (state, { payload }) => {},
  },
  extraReducers: builder => {
    // Drop an allocated row straight away, and keep it off until the read side agrees; the lists
    // and stats are refetched behind it.
    builder.addCase(walletEvents.allocateSuspenseAccount_success, (state, { meta }) => {
      if (meta?.txId && !state.suspenseAllocatedTxIds.includes(meta.txId)) {
        state.suspenseAllocatedTxIds.push(meta.txId)
      }
      const unallocated = state.suspenseAccountLists.unallocated
      const remaining = unallocated.items.filter(item => item.txId !== meta?.txId)
      if (remaining.length < unallocated.items.length) {
        unallocated.items = remaining
        unallocated.total = Math.max(0, unallocated.total - 1)
      }
    })
    // The note's read side lags the save, so the saved note is shown straight away rather than refetched.
    builder.addCase(walletEvents.saveSuspenseNote_success, (state, { payload }) => {
      const notes = payload.body.notes?.trim() || undefined
      Object.values(state.suspenseAccountLists).forEach(list => {
        const item = list.items.find(i => i.txId === payload.params.txId)
        if (item) {
          item.notes = notes
          item.notesUpdatedBy = notes ? payload.savedBy : undefined
          item.notesUpdatedById = undefined
          item.notesUpdatedAt = notes ? payload.savedAt : undefined
        }
      })
    })
  },
})

const { reducer, actions: events } = slice

export type ReportingEvent = ActionType<typeof events>

export { events, reducer }
